/**
 * 提醒中心。
 *
 * 「待通知」是这个页面的核心 —— 让「通知顾客」这件事有个终点，清单能归零。
 *
 * 措辞刻意避开「失败 / 未配置」：店主【决定】不做自动发送，手动发就是正常流程。
 * 把每天的正常工作报成故障，结果是这张清单先被无视，然后顾客真的没人通知。
 */

import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Check, Copy, MessageCircle, Phone, Undo2, X } from 'lucide-react'
import { api, type Me, type ReminderItem } from '../lib/api'
import { REMINDER_TYPE_ZH, date as fmtDate, time as fmtTime } from '../lib/format'
import { Badge, Button, Card, Chip, EmptyState, Skeleton, useToast } from '../components/ui'

type Tab = 'MANUAL_REQUIRED' | 'PENDING' | 'SENT'

export function RemindersPage({ me }: { me: Me }) {
  const qc = useQueryClient()
  const toast = useToast()
  const [tab, setTab] = useState<Tab>('MANUAL_REQUIRED')

  const counts = useQuery({
    queryKey: ['reminder-counts'],
    queryFn: () =>
      api.get<{ manualRequired: number; pending: number; channelHealthy: boolean; channelConfigured: boolean }>(
        '/api/reminders/counts',
      ),
  })

  const list = useQuery({
    queryKey: ['reminders', tab],
    queryFn: () => api.get<{ items: ReminderItem[] }>(`/api/reminders?state=${tab}`),
  })

  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['reminders'] })
    void qc.invalidateQueries({ queryKey: ['reminder-counts'] })
    void qc.invalidateQueries({ queryKey: ['today'] })
  }

  const unmark = useMutation({
    mutationFn: (id: string) => api.post(`/api/reminders/${id}/unmark-handled`),
    onSuccess: () => {
      refresh()
      toast.show('已退回待通知')
    },
  })

  /**
   * 通知完成。
   *
   * 由「点 WhatsApp」「点拨打」直接触发，不再要求事后补一次「标记已通知」——
   * 两步操作的第二步一定会被忘，清单于是永远不归零，最后整张清单被无视。
   *
   * 这是【乐观】的：wa.me 只是把 WhatsApp 打开并填好文案，浏览器拿不到任何回执，
   * 我们无从知道有没有真的按下发送。所以每次都带「撤销」。
   */
  const markHandled = useMutation({
    mutationFn: (v: { id: string; via?: 'WHATSAPP' | 'PHONE' | 'OTHER' }) =>
      api.post(`/api/reminders/${v.id}/mark-handled`, { via: v.via ?? 'OTHER' }),
    onSuccess: (_d, v) => {
      refresh()
      toast.show(
        v.via === 'WHATSAPP'
          ? '已记为「用 WhatsApp 通知了」'
          : v.via === 'PHONE'
            ? '已记为「打过电话了」'
            : '已标记为已通知',
        { undo: () => unmark.mutate(v.id) },
      )
    },
  })

  const cancel = useMutation({
    mutationFn: (id: string) => api.post(`/api/reminders/${id}/cancel`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['reminders'] })
      void qc.invalidateQueries({ queryKey: ['reminder-counts'] })
      toast.show('已取消')
    },
    onError: (e) => toast.show((e as Error).message, { tone: 'bad' }),
  })

  const items = list.data?.items ?? []
  const c = counts.data

  return (
    <div className="mx-auto max-w-[900px]">
      <h1 className="mb-4 text-[28px] font-bold">提醒中心</h1>

      {c && !c.channelConfigured && (
        <div className="mb-4 rounded-md border border-line bg-sunken px-4 py-3 text-[16px] text-ink-2">
          本店用手动发送：到点的提醒会出现在「待通知」，点一下 WhatsApp 就发，
          发完自动从清单消失。
        </div>
      )}

      <div className="mb-4 flex flex-wrap gap-2">
        <Chip active={tab === 'MANUAL_REQUIRED'} onClick={() => setTab('MANUAL_REQUIRED')}>
          待通知 {c ? `(${c.manualRequired})` : ''}
        </Chip>
        <Chip active={tab === 'PENDING'} onClick={() => setTab('PENDING')}>
          待发送 {c ? `(${c.pending})` : ''}
        </Chip>
        <Chip active={tab === 'SENT'} onClick={() => setTab('SENT')}>
          已发送
        </Chip>
      </div>

      {list.isLoading ? (
        <div className="flex flex-col gap-3">
          <Skeleton className="h-44" />
          <Skeleton className="h-44" />
        </div>
      ) : items.length === 0 ? (
        <Card>
          <EmptyState
            title={
              tab === 'MANUAL_REQUIRED'
                ? '今天没有要通知的顾客'
                : tab === 'PENDING'
                  ? '没有待发送的提醒'
                  : '还没有发送记录'
            }
            description={
              tab === 'MANUAL_REQUIRED' ? '到点要通知顾客的事会出现在这里。' : undefined
            }
          />
        </Card>
      ) : (
        <ul className="flex flex-col gap-3">
          {items.map((r) => (
            <li key={r.id}>
              <div
                className={
                  'rounded-md border bg-white shadow-xs ' +
                  (tab === 'MANUAL_REQUIRED'
                    ? 'border-l-[3px] border-line border-l-brand-600'
                    : 'border-line')
                }
              >
                <div className="px-5 py-4">
                  <div className="flex flex-wrap items-baseline justify-between gap-2">
                    <span className="text-[19px] font-semibold">{r.customer.fullName}</span>
                    <span className="tnum text-[15px] text-ink-3">{r.toPhoneE164}</span>
                  </div>

                  <p className="tnum text-[15px] text-ink-2">
                    {REMINDER_TYPE_ZH[r.type] ?? r.type} · {fmtDate(r.scheduledAt)}{' '}
                    {fmtTime(r.scheduledAt)}
                    {r.case && ` · ${r.case.caseNo}`}
                  </p>

                  {/* 通道未配置是【选择】不是故障，不该每条都红着脸报一次错。
                      真正的失败（发出去被拒、号码不通）才值得标红。 */}
                  {r.errorMessage && tab !== 'SENT' && r.attempts > 0 && (
                    <p className="mt-2 flex items-center gap-2 rounded-sm bg-bad-bg px-3 py-2 text-[15px] text-bad">
                      <span className="flex-1">{r.errorMessage}</span>
                      <span className="shrink-0 text-[13px]">已尝试 {r.attempts} 次</span>
                    </p>
                  )}

                  {/* 已通知的凭据：顾客说「我没收到」时，这里是唯一能回溯的东西 */}
                  {tab === 'SENT' && r.handledAt && (
                    <p className="tnum mt-2 rounded-sm bg-ok-bg px-3 py-2 text-[15px] text-ok">
                      {r.handledVia === 'WHATSAPP'
                        ? '用 WhatsApp 通知'
                        : r.handledVia === 'PHONE'
                          ? '打电话通知'
                          : '手动标记已通知'}
                      {r.handledByName ? ` · ${r.handledByName}` : ''} · {fmtDate(r.handledAt)}{' '}
                      {fmtTime(r.handledAt)}
                    </p>
                  )}

                  {/* 完整文案，可直接复制粘到 WhatsApp */}
                  <pre className="mt-3 max-h-32 overflow-y-auto whitespace-pre-wrap rounded-sm bg-sunken px-3 py-2.5 font-sans text-[15px] leading-relaxed text-ink-2">
                    {r.body}
                  </pre>

                  <div className="mt-3 flex flex-wrap gap-2">
                    {tab === 'MANUAL_REQUIRED' && (
                      <>
                        {/* 主操作是 WhatsApp —— 实际上几乎都是这么通知的，
                            电话是号码打不通时的兜底 */}
                        <Button
                          variant="primary"
                          size="sm"
                          icon={<MessageCircle size={15} />}
                          onClick={() => {
                            window.open(
                              `https://wa.me/${r.toPhoneE164.replace('+', '')}?text=${encodeURIComponent(r.body)}`,
                              '_blank',
                              'noopener',
                            )
                            markHandled.mutate({ id: r.id, via: 'WHATSAPP' })
                          }}
                        >
                          用 WhatsApp 通知
                        </Button>
                        <Button
                          size="sm"
                          icon={<Phone size={15} />}
                          onClick={() => {
                            window.open(`tel:${r.toPhoneE164}`)
                            markHandled.mutate({ id: r.id, via: 'PHONE' })
                          }}
                        >
                          拨打
                        </Button>
                      </>
                    )}
                    <Button
                      size="sm"
                      icon={<Copy size={15} />}
                      onClick={() => {
                        void navigator.clipboard.writeText(r.body)
                        toast.show('文案已复制')
                      }}
                    >
                      复制文案
                    </Button>
                    {tab === 'MANUAL_REQUIRED' && (
                      /* 「用别的方式通知了」的兜底：当面说了、托家属带话、
                         或者已经在别处发过。上面两个按钮已经会自动标记。 */
                      <Button
                        size="sm"
                        icon={<Check size={15} />}
                        loading={markHandled.isPending}
                        onClick={() => markHandled.mutate({ id: r.id })}
                      >
                        已经通知过了
                      </Button>
                    )}
                    {tab === 'SENT' && r.handledAt && (
                      <Button
                        size="sm"
                        variant="ghost"
                        icon={<Undo2 size={15} />}
                        loading={unmark.isPending}
                        onClick={() => unmark.mutate(r.id)}
                      >
                        退回待通知
                      </Button>
                    )}
                    {tab === 'PENDING' && me.user.role === 'OWNER' && (
                      <Button
                        size="sm"
                        icon={<X size={15} />}
                        loading={cancel.isPending}
                        onClick={() => cancel.mutate(r.id)}
                      >
                        取消
                      </Button>
                    )}
                    {tab === 'SENT' && r.sentAt && (
                      <Badge tone="ok">
                        已送达 {fmtDate(r.sentAt)} {fmtTime(r.sentAt)}
                      </Badge>
                    )}
                  </div>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
